import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { canCancel, canPauseResume, cancelJob, deleteJob, downloadInput, downloadMetadata, downloadOutput, formatEta, pauseJob, resumeJob, } from '../hooks/useJobs';
const STATUS_TONE = {
    queued: 'badge badge-neutral',
    running: 'badge badge-info',
    paused: 'badge badge-warning',
    succeeded: 'badge badge-success',
    failed: 'badge badge-danger',
    cancelled: 'badge badge-muted',
};
function formatBytes(n) {
    // input_bytes es BIGINT en Postgres → node-postgres lo devuelve como STRING.
    // Sin coaccionar, un valor < 1 KiB no entra al while y `v.toFixed` crashea
    // (mismo bug que /ecosystem). Coaccionar a número lo blinda.
    const v0 = Number(n);
    if (!Number.isFinite(v0) || v0 <= 0)
        return '—';
    const k = 1024;
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let v = v0;
    while (v >= k && i < units.length - 1) {
        v /= k;
        i += 1;
    }
    return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
}
function elapsedFrom(iso) {
    if (!iso)
        return null;
    return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
}
/** Estima los segundos restantes en vivo: ETA inicial menos el tiempo
 *  ya transcurrido, ajustado por el progreso real. */
function liveEta(job) {
    if (job.status !== 'running' || job.eta_seconds === null)
        return null;
    const elapsed = elapsedFrom(job.started_at) ?? 0;
    // Si tenemos progreso > 5%, extrapolamos: t_total = elapsed / (pct/100)
    if (job.progress > 5) {
        const total = elapsed / (job.progress / 100);
        return Math.max(0, Math.round(total - elapsed));
    }
    return Math.max(0, job.eta_seconds - elapsed);
}
export default function JobCard({ job, onChange }) {
    const { t } = useTranslation();
    const isDone = job.status === 'succeeded';
    const isRunning = job.status === 'running' || job.status === 'queued' || job.status === 'paused';
    const result = (job.result ?? {});
    const outputBytes = typeof result.mp4_bytes === 'number'
        ? result.mp4_bytes
        : typeof result.output_bytes === 'number'
            ? result.output_bytes
            : undefined;
    const sha = typeof result.sha256 === 'string' ? result.sha256 : null;
    const eta = liveEta(job);
    const onDownloadOutput = async () => { try {
        await downloadOutput(job);
    }
    catch (e) {
        console.error(e);
    } };
    const onDownloadInput = async () => { try {
        await downloadInput(job);
    }
    catch (e) {
        console.error(e);
    } };
    const onDownloadMeta = async () => { try {
        await downloadMetadata(job);
    }
    catch (e) {
        console.error(e);
    } };
    const onDelete = async () => {
        if (!confirm(t('dashboard.confirmDelete')))
            return;
        try {
            await deleteJob(job.id);
            onChange?.();
        }
        catch (e) {
            console.error(e);
        }
    };
    const onPause = async () => { try {
        await pauseJob(job.id);
        onChange?.();
    }
    catch (e) {
        console.error(e);
    } };
    const onResume = async () => { try {
        await resumeJob(job.id);
        onChange?.();
    }
    catch (e) {
        console.error(e);
    } };
    const onCancel = async () => {
        if (!confirm(t('jobControl.confirmCancel')))
            return;
        try {
            await cancelJob(job.id);
            onChange?.();
        }
        catch (e) {
            console.error(e);
        }
    };
    return (_jsxs("article", { className: "job-card", children: [_jsxs("header", { children: [_jsx("span", { className: "job-kind", children: t(`dashboard.kind.${job.kind}`) }), _jsx("span", { className: STATUS_TONE[job.status], children: t(`dashboard.status.${job.status}`) }), _jsx(Link, { to: `/jobs/${job.id}`, className: "job-id", title: job.id, children: job.id.slice(0, 8) })] }), isRunning && (_jsxs("div", { className: "progress-block", children: [_jsx("div", { className: "progress-bar", role: "progressbar", "aria-valuenow": job.progress, "aria-valuemin": 0, "aria-valuemax": 100, children: _jsx("div", { className: "progress-fill", style: { width: `${job.progress}%` } }) }), _jsxs("div", { className: "progress-meta", children: [_jsxs("span", { className: "progress-pct", children: [job.progress, "%"] }), job.current_step && _jsx("span", { className: "progress-step", children: job.current_step }), eta !== null && _jsxs("span", { className: "progress-eta", children: ["ETA ", formatEta(eta)] })] }), job.progress_msg && _jsx("p", { className: "progress-msg muted", children: job.progress_msg })] })), _jsxs("dl", { className: "job-meta", children: [_jsx("dt", { children: t('dashboard.created') }), _jsx("dd", { children: new Date(job.created_at).toLocaleString() }), job.input_bytes != null && (_jsxs(_Fragment, { children: [_jsx("dt", { children: t('dashboard.inputSize') }), _jsx("dd", { children: formatBytes(job.input_bytes) })] })), outputBytes !== undefined && (_jsxs(_Fragment, { children: [_jsx("dt", { children: t('dashboard.outputSize') }), _jsx("dd", { children: formatBytes(outputBytes) })] })), sha && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "SHA-256" }), _jsxs("dd", { className: "mono trunc", title: sha, children: [sha.slice(0, 16), "\u2026"] })] })), job.error_message && (_jsxs(_Fragment, { children: [_jsx("dt", { children: t('common.error') }), _jsx("dd", { className: "error", children: job.error_message.slice(0, 200) })] }))] }), _jsxs("footer", { children: [canPauseResume(job) && job.status === 'running' && (_jsxs("button", { type: "button", className: "btn btn-ghost", onClick: () => void onPause(), children: ["\u23F8 ", t('jobControl.pause')] })), canPauseResume(job) && job.status === 'paused' && (_jsxs("button", { type: "button", className: "btn btn-primary", onClick: () => void onResume(), children: ["\u25B6 ", t('jobControl.resume')] })), canCancel(job) && (_jsxs("button", { type: "button", className: "btn btn-ghost", onClick: () => void onCancel(), children: ["\u23F9 ", t('jobControl.cancel')] })), isDone && (_jsx("button", { type: "button", className: "btn btn-primary", onClick: () => void onDownloadOutput(), children: t('dashboard.download') })), job.input_path && (_jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => void onDownloadInput(), title: t('dashboard.downloadInput'), children: t('dashboard.downloadInput') })), _jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => void onDownloadMeta(), title: t('dashboard.downloadMetadata'), children: "JSON" }), _jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => void onDelete(), children: t('dashboard.delete') })] })] }));
}
